import { findIndex } from "@fluentui/react/lib/Utilities";

import { defaultLargeButtonMinWidth } from "@1js/acui-button";

import type { EstimatedRibbonWidth } from ".";
import {
  getCoreRibbonElementById,
  getElementWidth,
  getPreviewGalleryGhostFlyout,
  getRibbonElementTextContainer,
  getRibbonPreviewGalleryItems,
} from ".";
import type {
  BuildTimeControlWidths,
  EstimatedScalingWidthDict,
  RibbonScalingStateMLR,
  VirtualRibbonScalingStateMLR,
} from "..";
import { chevronWidth, ControlState, scaleRibbonDown } from "..";
import { setEstimatedScalingWidths } from "../estimatedWidths";
import type { RibbonControlProps } from "../../../Controls/RibbonControlProps";
import type {
  RibbonScalingStep,
  RibbonScalingTransformation,
} from "../../../Scaling";
import type { RibbonControlGroupDefinitionMLR } from "../../Ribbon/Ribbon.Props";
import type {
  Layout,
  LayoutDivider,
  MultiRowSection,
  MultiRowSectionItem,
  Section,
  SingleRowSection,
  SingleRowSectionItem,
  VerticalControlStack,
} from "../../RibbonControlGroupMLR";
import {
  rowDividerWidth,
  sectionDividerWidth,
} from "../../RibbonControlGroupMLR";
import {
  getDefaultLayout,
  sectionSpacerWidth,
} from "../../RibbonControlGroupMLR/layoutHelpers";
import { tabContentContainerPadding } from "../RibbonBottomBarConstants";
import type { AppContextType } from "@1js/acui-context";
import { vNextRibbonModeSwitcherLeftRightMargin } from "../../../ribbonStylingConstantsBase";

/**
 * Initializes the ribbon's VirtualRibbonScalingState for the MLR
 *
 * @param scalingState The initial scalingState used to populate the virtual scaling state
 * @param knownWidths The control dimensions determined at build-time
 */
export function initializeVirtualScalingStateMLR(
  scalingState: RibbonScalingStateMLR,
  knownWidths?: BuildTimeControlWidths
): VirtualRibbonScalingStateMLR {
  const virtualScalingState: VirtualRibbonScalingStateMLR = {
    groups: {},
    controls: {},
    knownWidths,
  };

  scalingState.scaledRibbonGroups.forEach((group) => {
    virtualScalingState.groups[group.id] = {
      layoutIndex: 0,
      isFlyout: false,
    };
  });

  if (knownWidths) {
    for (const id in knownWidths) {
      if (controlIsInRibbonDefinitionMLR(id, scalingState)) {
        const previewGalleryItems = knownWidths[id].previewGalleryItems;
        virtualScalingState.controls[id] = {
          fullControlWidth: knownWidths[id].componentRoot,
          labelWidth: knownWidths[id].textContainer,
          previewGalleryItemWidths: previewGalleryItems
            ? [...previewGalleryItems]
            : undefined,
          ghostFlyout: knownWidths[id].ghostFlyout,
          ghostFlyoutTextContainer: knownWidths[id].ghostFlyoutTextContainer,
          groupId: controlGroupById(id, scalingState),
        };
      }
    }
  }

  return virtualScalingState;
}

/**
 * Estimates the impact of applying every scaling step on the current tab, and fills the estimatedWidths dict with these values
 *
 * @param scalingState The current scaling state for the ribbon. This holds all data necessary to render and transform the ribbon
 * @param measuredDimension The width of the ribbon with only the initial scaling steps applied. This is the base value we will count down from
 * @param draftVirtualScalingState the virtual scaling state, which contains measurements obtained at build-time
 */
export function estimateRibbonScalingStepsMLR(
  scalingState: RibbonScalingStateMLR,
  measuredDimension: number,
  appContext: AppContextType,
  draftVirtualScalingState?: VirtualRibbonScalingStateMLR
) {
  const estimatedWidths: EstimatedScalingWidthDict = {};
  let nextScalingStepIndex = scalingState.appliedScalingStepCount;
  let currentWidth = measuredDimension;
  if (!draftVirtualScalingState) {
    // eslint-disable-next-line no-param-reassign
    draftVirtualScalingState = initializeVirtualScalingStateMLR(scalingState);
  }

  estimatedWidths[nextScalingStepIndex] = currentWidth;
  nextScalingStepIndex = nextScalingStepIndex + 1;

  while (nextScalingStepIndex < scalingState.scalingSteps.length) {
    currentWidth -= calculatePixelsSavedFromScalingStepMLR(
      scalingState,
      // Use the width of one scaling step prior because that will
      // be the max width of the next scaling step
      scalingState.scalingSteps[nextScalingStepIndex - 1],
      draftVirtualScalingState,
      appContext
    );

    estimatedWidths[nextScalingStepIndex] = currentWidth;
    nextScalingStepIndex += 1;
  }

  return estimatedWidths;
}

/**
 * Estimates the number of pixels saved by applying a given scaling step to the ribbon
 *
 * @param scalingState The current scaling state for the ribbon. This holds all data necessary to render and transform the ribbon
 * @param scalingStep The scaling step that is being applied
 * @param virtualScalingState Information used to keep track of virtual changes to the DOM caused by previously estimated scaling steps
 * @returns The number of pixels by which the ribbon contents will shrink after applying the given scaling step
 */
function calculatePixelsSavedFromScalingStepMLR(
  scalingState: RibbonScalingStateMLR,
  scalingStep: Readonly<RibbonScalingStep>,
  draftVirtualScalingState: VirtualRibbonScalingStateMLR,
  appContext: AppContextType
): number {
  if (Array.isArray(scalingStep)) {
    return scalingStep.reduce(
      (accumulator, scalingStep) =>
        accumulator +
        calculatePixelsSavedFromScalingStepMLR(
          scalingState,
          scalingStep,
          draftVirtualScalingState,
          appContext
        ),
      0
    );
  }
  const step = scalingStep as RibbonScalingTransformation;

  switch (step.type) {
    case "DropLabel":
      return calculatePixelsSavedFromDropLabelStep(
        step.controlId,
        scalingState,
        draftVirtualScalingState
      );
    case "GroupToFlyout":
      return calculatePixelsSavedFromGroupToFlyoutStep(
        step.groupId,
        scalingState,
        draftVirtualScalingState
      );
    case "ChangeGroupLayout":
      return calculatePixelsSavedFromChangeLayoutStep(
        step.groupId,
        scalingState,
        draftVirtualScalingState
      );
    case "DropPreviewItemFromGallery":
      return pixelsSavedFromDropPreviewItemFromGalleryStep(
        step.controlId,
        draftVirtualScalingState
      );
    case "GalleryToFlyout":
      return pixelsSavedFromGalleryToFlyoutStep(
        step.controlId,
        draftVirtualScalingState
      );
  }

  return 0;
}

/**
 * The 'DropLabel' scaling step will result in the removal of a control's label. The effect of this step can be
 * estimated by determining the width of the control group with and without the label, and returning the difference
 */
function calculatePixelsSavedFromDropLabelStep(
  controlId: string,
  scalingState: RibbonScalingStateMLR,
  draftVirtualScalingState: VirtualRibbonScalingStateMLR
) {
  // Find the group definition
  const controlGroupIndex = findIndex(
    scalingState.ribbonGroupDefinitions.groups,
    (group: RibbonControlGroupDefinitionMLR) => {
      return group.controls.some((control) => control.id === controlId);
    }
  );
  if (controlGroupIndex === -1) {
    return 0;
  }
  const group = scalingState.ribbonGroupDefinitions.groups[controlGroupIndex];

  // Determine the width of the current group
  const currentGroupWidth =
    draftVirtualScalingState.groups[group.id]?.width ??
    getLayoutLength(
      chooseCorrectGroupLayout(group, draftVirtualScalingState),
      draftVirtualScalingState,
      group.controls
    );

  // Determine the effect of the scaling step on the given control
  const currentControlWidth = getControlWidthMLR(
    controlId,
    draftVirtualScalingState
  );
  // Task 6294354: ResponsiveScaling: Investigate utilizing ensureFullWidths for MLR
  const newControlWidth =
    currentControlWidth -
    (draftVirtualScalingState.controls[controlId].labelWidth ??
      getElementWidth(
        getRibbonElementTextContainer(getCoreRibbonElementById(controlId))
      ) ??
      0);

  // Store the newly scaled control's width
  draftVirtualScalingState.controls[controlId] = {
    ...draftVirtualScalingState.controls[controlId],
    fullControlWidth: newControlWidth,
  };

  // make sure that all layout variants are updated as well
  let index = 1;
  while (true) {
    const control = draftVirtualScalingState.controls[`${controlId}-${index}`];
    if (!control) {
      break;
    }
    if (control.labelWidth) {
      control.fullControlWidth -= control.labelWidth;
      control.labelWidth = 0;
    }
    index += 1;
  }

  // Determine the width of the group after applying the scaling step
  const newGroupWidth = getLayoutLength(
    chooseCorrectGroupLayout(group, draftVirtualScalingState),
    draftVirtualScalingState,
    group.controls
  );

  // Store the newly scaled group's width
  draftVirtualScalingState.groups[group.id] = {
    ...draftVirtualScalingState.groups[group.id],
    width: newGroupWidth,
  };
  return currentGroupWidth - newGroupWidth;
}

/**
 * The 'GroupToFlyout' scaling step will convert the specified control group into a FlyoutAnchor. The effect of this step can
 * can be estimated by returning the difference in width between the outgoing control group and the incoming FlyoutAnchor.
 */
function calculatePixelsSavedFromGroupToFlyoutStep(
  groupId: string,
  scalingState: RibbonScalingStateMLR,
  draftVirtualScalingState: VirtualRibbonScalingStateMLR
) {
  // Find the group definition
  const controlGroupIndex = findIndex(
    scalingState.ribbonGroupDefinitions.groups,
    (group: RibbonControlGroupDefinitionMLR) => group.id === groupId
  );
  if (controlGroupIndex === -1) {
    return 0;
  }
  const group = scalingState.ribbonGroupDefinitions.groups[controlGroupIndex];
  const scaledGroup = scalingState.scaledRibbonGroups.find(
    (group) => group.id === groupId
  );

  // Determine the width of the current group
  const currentGroupWidth =
    draftVirtualScalingState.groups[groupId]?.width ??
    getLayoutLength(
      chooseCorrectGroupLayout(group, draftVirtualScalingState),
      draftVirtualScalingState,
      group.controls
    );

  // Determine the width of the incoming flyout
  const groupFlyoutWidth =
    draftVirtualScalingState.controls[groupId]?.fullControlWidth ??
    (getControlWidthMLR(
      groupId,
      draftVirtualScalingState,
      scaledGroup?.controls
    ) ||
      defaultLargeButtonMinWidth);

  // Update the draftVirtualScalingState with the current scaling state
  draftVirtualScalingState.groups[groupId].isFlyout = true;
  draftVirtualScalingState.groups[groupId].width = groupFlyoutWidth;

  return currentGroupWidth - groupFlyoutWidth;
}

/**
 * The 'ChangeLayout' scaling step will convert the current layout with another one The effect of this step can
 * can be estimated by returning the difference in width between the outgoing layout and the incoming layout.
 */
function calculatePixelsSavedFromChangeLayoutStep(
  groupId: string,
  scalingState: RibbonScalingStateMLR,
  draftVirtualScalingState: VirtualRibbonScalingStateMLR
) {
  // Find the group definition
  const controlGroupIndex = findIndex(
    scalingState.ribbonGroupDefinitions.groups,
    (group: RibbonControlGroupDefinitionMLR) => group.id === groupId
  );
  if (controlGroupIndex === -1) {
    return 0;
  }
  const group = scalingState.ribbonGroupDefinitions.groups[controlGroupIndex];

  // Get the relevant layout definitions and determine their lengths
  const currentLayout = chooseCorrectGroupLayout(
    group,
    draftVirtualScalingState
  );
  const newLayout = chooseCorrectGroupLayout(
    group,
    draftVirtualScalingState,
    true /* findNextAvailableLayout */
  );

  const currentLayoutLength =
    draftVirtualScalingState.groups[groupId]?.width ??
    getLayoutLength(currentLayout, draftVirtualScalingState, group.controls);

  // Update the layoutIndex so it's accounted for when getting the length of the new layout
  let layoutIndexUpdated = false;
  draftVirtualScalingState.groups[groupId] = {
    ...draftVirtualScalingState.groups[groupId],
    layoutIndex: draftVirtualScalingState.groups[groupId].layoutIndex + 1,
  };
  layoutIndexUpdated = true;

  const newLayoutLength = getLayoutLength(newLayout, draftVirtualScalingState);

  // Update the draftVirtualScalingState with the current scaling state
  draftVirtualScalingState.groups[groupId] = {
    ...draftVirtualScalingState.groups[groupId],
    layoutIndex: layoutIndexUpdated
      ? draftVirtualScalingState.groups[groupId].layoutIndex
      : draftVirtualScalingState.groups[groupId].layoutIndex + 1,
    width: newLayoutLength,
  };

  return currentLayoutLength - newLayoutLength;
}

/**
 * The 'DropPreviewItemFromGallery' scaling step will remove the last visible item from the preview gallery. Since preview gallery
 * items can each be rendered with a different size, it is important to measure the specific button being dropped. The effect of this
 * step can be estimated by determining which button is being dropped and returning its width
 */
function pixelsSavedFromDropPreviewItemFromGalleryStep(
  controlId: string,
  draftVirtualScalingState: VirtualRibbonScalingStateMLR
) {
  const previewGalleryWidth =
    draftVirtualScalingState.controls[controlId]?.fullControlWidth ??
    getControlWidthMLR(controlId, draftVirtualScalingState);

  /* calculate the width of each gallery button if we haven't already done so */
  if (!draftVirtualScalingState.controls[controlId]?.previewGalleryItemWidths) {
    const galleryButtonElements =
      getRibbonPreviewGalleryItems(getCoreRibbonElementById(controlId)) ?? [];
    const galleryButtonWidths = [];
    for (let i = 0; i < galleryButtonElements.length; i += 1) {
      galleryButtonWidths.push(getElementWidth(galleryButtonElements[i]) || 0);
    }
    draftVirtualScalingState.controls[controlId] = {
      ...draftVirtualScalingState.controls[controlId],
      previewGalleryItemWidths: galleryButtonWidths,
    };
  }

  const galleryButtonWidth =
    draftVirtualScalingState.controls[
      controlId
    ].previewGalleryItemWidths?.pop();

  /* If we can't find the control, this scaling step will have no effect */
  if (!previewGalleryWidth || !galleryButtonWidth) {
    return 0;
  }

  /* Update the virtualScalingState with the new state of the ribbon */
  draftVirtualScalingState.controls[controlId] = {
    ...draftVirtualScalingState.controls[controlId],
    fullControlWidth: previewGalleryWidth - galleryButtonWidth,
    labelWidth: 0,
    controlState: ControlState.PartialGallery,
  };

  return galleryButtonWidth;
}

/**
 * The 'GalleryToFlyout' scaling step will convert the specified PreviewGallery into a FlyoutAnchor. The effect of this step can
 * can be estimated by returning the difference in width between the outgoing PreviewGallery and the incoming FlyoutAnchor. The
 * FlyoutAnchor needs to have been rendered hidden so that its width can be accurately estimated.
 */
function pixelsSavedFromGalleryToFlyoutStep(
  controlId: string,
  draftVirtualScalingState: VirtualRibbonScalingStateMLR
) {
  const draftControl = draftVirtualScalingState.controls[controlId];
  const galleryWidth =
    draftControl?.fullControlWidth ??
    getControlWidthMLR(controlId, draftVirtualScalingState);
  const ghostFlyout = getPreviewGalleryGhostFlyout(
    getCoreRibbonElementById(controlId)?.parentElement,
    controlId
  );
  const galleryFlyoutWidth =
    draftControl?.ghostFlyout ?? getElementWidth(ghostFlyout);
  const galleryLabel = getRibbonElementTextContainer(ghostFlyout);
  const ghostLabelWidth =
    draftControl?.ghostFlyoutTextContainer ?? getElementWidth(galleryLabel);

  /* If we can't find the control, this scaling step will have no effect */
  if (
    !galleryWidth ||
    !galleryFlyoutWidth ||
    draftControl?.previewGalleryItemWidths?.length === 0
  ) {
    return 0;
  }

  /* Update the virtualScalingState with the new state of the ribbon */
  draftVirtualScalingState.controls[controlId] = {
    ...draftControl,
    fullControlWidth: galleryFlyoutWidth,
    labelWidth: ghostLabelWidth,
    controlState: ControlState.FullControl,
    previewGalleryItemWidths: [],
  };

  return galleryWidth - galleryFlyoutWidth;
}

/**
 * Returns the width of a given control
 */
function getControlWidthMLR(
  controlId: string,
  draftVirtualScalingState: VirtualRibbonScalingStateMLR,
  availableControls?: RibbonControlProps[]
) {
  if (
    availableControls &&
    !availableControls.some((control) => control.id === controlId)
  ) {
    return 0;
  }

  const groupLayoutIndex =
    draftVirtualScalingState.groups[
      draftVirtualScalingState.controls[controlId]?.groupId || ""
    ]?.layoutIndex;
  if (
    groupLayoutIndex &&
    !!draftVirtualScalingState.controls[`${controlId}-${groupLayoutIndex}`]
  ) {
    // if we have an entry for this specific layout variation, use it.
    // eslint-disable-next-line no-param-reassign
    controlId = `${controlId}-${groupLayoutIndex}`;
  }

  const elementWidth =
    draftVirtualScalingState.controls[controlId]?.fullControlWidth ??
    getElementWidth(getCoreRibbonElementById(controlId)) ??
    0;

  draftVirtualScalingState.controls[controlId] = {
    ...draftVirtualScalingState.controls[controlId],
    fullControlWidth: elementWidth,
  };

  // If we got an element width, have known widths, and do not currently have the info stored,
  // for this controlId add it to known widths
  if (
    elementWidth &&
    draftVirtualScalingState.knownWidths &&
    !draftVirtualScalingState.knownWidths[controlId]
  ) {
    const controlData = draftVirtualScalingState.controls[controlId];
    draftVirtualScalingState.knownWidths[controlId] = {
      componentRoot: controlData.fullControlWidth,
      ghostFlyout: controlData.ghostFlyout,
      ghostFlyoutTextContainer: controlData.ghostFlyoutTextContainer,
      previewGalleryItems: controlData.previewGalleryItemWidths,
      textContainer: controlData.labelWidth,
    };
  }

  return elementWidth;
}

/**
 * Determines the correct layout to use for a group's measurement. If there isn't a layout specified, a single row of controls is rendered
 */
function chooseCorrectGroupLayout(
  group: RibbonControlGroupDefinitionMLR,
  draftVirtualScalingState: VirtualRibbonScalingStateMLR,
  findNextAvailableLayout?: boolean
) {
  const currentLayoutIndex =
    draftVirtualScalingState.groups[group.id]?.layoutIndex ?? 0;
  return (
    group.layouts?.[
      findNextAvailableLayout ? currentLayoutIndex + 1 : currentLayoutIndex
    ] ?? getDefaultLayout(group.controls)
  );
}

/**
 * Returns true if a given section is a divider
 */
function isDivider(section: any) {
  return typeof section === "object" && section.type === "Divider";
}

/**
 * Several of the MLR layout widths are driven by their widest item's width. Rather than repeating this
 * logic multiple times throughout this file, I'm extracting it into a small util.
 */
function getLargestNumericValue(
  iterableObject: any,
  getItemWidth: (item: any) => number
) {
  let largestValue = 0;
  iterableObject.forEach((item: any) => {
    const currentValue = getItemWidth(item);
    if (currentValue > largestValue) {
      largestValue = currentValue;
    }
  });
  return largestValue;
}

/**
 * Determines the length of a given control stack
 */
function getControlStackLength(
  stack: VerticalControlStack,
  draftVirtualScalingState: VirtualRibbonScalingStateMLR,
  availableControls?: RibbonControlProps[]
): number {
  // The width of a control stack is the width of the largest control in the stack;
  return getLargestNumericValue(stack, (stackItem: string | null) => {
    return stackItem === null
      ? 0
      : getControlWidthMLR(
          stackItem,
          draftVirtualScalingState,
          availableControls
        );
  });
}

/**
 * Determines the length of a single row section. This type of section has a length equal to the
 * sum it its items
 */
function getSingleRowSectionLength(
  section: SingleRowSection,
  draftVirtualScalingState: VirtualRibbonScalingStateMLR,
  availableControls?: RibbonControlProps[]
): number {
  return section.reduce(
    (totalWidth: number, sectionItems: SingleRowSectionItem[]) => {
      let prevSectionItem: "stack" | "control" | undefined = undefined;
      return (
        totalWidth +
        sectionItems.reduce(
          (totalItemWidth: number, singleRowItem: SingleRowSectionItem) => {
            if (isDivider(section)) {
              return totalItemWidth + rowDividerWidth;
            }
            if (Array.isArray(singleRowItem)) {
              // This item is a vertical stack
              const spacerWidth =
                prevSectionItem === "control" ? sectionSpacerWidth : 0;
              prevSectionItem = "stack";
              return (
                totalItemWidth +
                spacerWidth +
                getControlStackLength(
                  singleRowItem,
                  draftVirtualScalingState,
                  availableControls
                )
              );
            }
            if (typeof singleRowItem === "string") {
              // Single large control
              const spacerWidth =
                prevSectionItem === "stack" ? sectionSpacerWidth : 0;
              prevSectionItem = "control";
              return (
                totalItemWidth +
                spacerWidth +
                getControlWidthMLR(
                  singleRowItem,
                  draftVirtualScalingState,
                  availableControls
                )
              );
            }
            return totalItemWidth;
          },
          0
        )
      );
    },
    0
  );
}

/**
 * Determines the length of a multi-row section. This type of section has a length equal to the
 * width of its largest row
 */
function getMultiRowLayoutSectionLength(
  section: MultiRowSection,
  draftVirtualScalingState: VirtualRibbonScalingStateMLR,
  availableControls?: RibbonControlProps[]
): number {
  // The width of a MultiRowLayout is the width of the largest row in the layout
  return getLargestNumericValue(section, (row: MultiRowSectionItem[]) => {
    // A row has a width equal to the sum of its items
    return row.reduce((totalWidth: number, control: MultiRowSectionItem) => {
      if (isDivider(section)) {
        return totalWidth + rowDividerWidth;
      }
      return (
        totalWidth +
        getControlWidthMLR(
          control as string,
          draftVirtualScalingState,
          availableControls
        )
      );
    }, 0);
  });
}

/**
 * Determines the length of a given layout section
 */
function getLayoutSectionLength(
  section: Section | LayoutDivider,
  draftVirtualScalingState: VirtualRibbonScalingStateMLR,
  availableControls?: RibbonControlProps[]
): number {
  if (isDivider(section)) {
    return rowDividerWidth;
  }
  if (Array.isArray(section)) {
    if (section.length === 1) {
      // Single row section
      return getSingleRowSectionLength(
        section,
        draftVirtualScalingState,
        availableControls
      );
    }
    // Multi-row section
    return getMultiRowLayoutSectionLength(
      section,
      draftVirtualScalingState,
      availableControls
    );
  }
  throw new Error("Unknown section type: " + section);
}

/**
 * Determines the length of a given layout. A layout's width is equal to the sum it it's section widths
 */
function getLayoutLength(
  layout: Layout,
  draftVirtualScalingState: VirtualRibbonScalingStateMLR,
  availableControls?: RibbonControlProps[]
) {
  // A Layout width is equal to the sum of it's section widths
  return layout?.sections.reduce(
    (totalWidth: number, section: Section | LayoutDivider) => {
      return (
        totalWidth +
        getLayoutSectionLength(
          section,
          draftVirtualScalingState,
          availableControls
        )
      );
    },
    0
  );
}

/**
 * Estimates the width of the bottom bar based on known control dimensions determined at build-time
 */
export function getRibbonBottomBarWidthMLR(
  scalingState: RibbonScalingStateMLR,
  draftVirtualScalingState: VirtualRibbonScalingStateMLR,
  additionalTotalWidth?: number
): EstimatedRibbonWidth {
  const unknownIds: string[] = [];
  let totalIdCount: number = 0;
  const ribbonWidth = scalingState.scaledRibbonGroups.reduce(
    (accumulator, group) => {
      totalIdCount = totalIdCount + group.controls.length;
      return (
        accumulator +
        getLayoutLength(
          group.layout ?? getDefaultLayout(group.controls),
          draftVirtualScalingState,
          group.controls
        ) +
        sectionDividerWidth
      );
    },
    0
  );

  return {
    width: ribbonWidth + (additionalTotalWidth ?? 0),
    unknownIds,
    totalIdCount,
  };
}

/**
 * Returns true if a given controlId is in the current ribbon definition. This is used to
 * filter out controls that may have been trimmed at boot. In these scenarios, we need to know
 * that a scaling step on these controls has no effect. In the event that a full control group is
 * rendered as a flyout anchor, that group's ID becomes the ID of the control
 */
function controlIsInRibbonDefinitionMLR(
  controlId: string,
  scalingState: RibbonScalingStateMLR
) {
  let id = controlId;
  if (controlId.includes("-")) {
    // layout-impacted control definitions need to be trimmed for this check
    id = controlId.split("-")[0];
  }

  return scalingState.scaledRibbonGroups.some((group) => {
    return (
      group.controls.some((control) => {
        return control.id === id;
      }) || group.id === id
    );
  });
}
function controlGroupById(
  controlId: string,
  scalingState: RibbonScalingStateMLR
): string | undefined {
  return scalingState.scaledRibbonGroups.filter((group) => {
    return group.controls.some((control) => {
      return control.id === controlId;
    });
  })?.[0]?.id;
}

/**
 * Determines the scalingState to be rendered given the buildTimeControlWidths
 */
export function scaleRibbonDownWithBuildTimeScalingMLR(
  scalingState: RibbonScalingStateMLR,
  knownWidths: BuildTimeControlWidths,
  reservedSpace: number,
  onWarning: ((message: string) => void) | undefined,
  appContext: AppContextType
) {
  // Initialize the virtualScalingState and fill it with our known widths
  const draftVirtualScalingState = initializeVirtualScalingStateMLR(
    scalingState,
    knownWidths
  );

  // Determine the width of the ribbon
  const scalingData = getRibbonBottomBarWidthMLR(
    scalingState,
    draftVirtualScalingState,
    tabContentContainerPadding
  );

  // Log errors on controls that we tried to estimate but couldn't
  scalingData.unknownIds.forEach((controlId) => {
    onWarning?.(`Trying to estimate unknown width. ControlId: ${controlId}`);
  });

  // Determine the effect of applying each scaling step
  const estimatedWidths = estimateRibbonScalingStepsMLR(
    scalingState,
    scalingData.width,
    appContext,
    draftVirtualScalingState
  );

  // Set the calculated estimatedWidths so they don't have to be re-computed by the quickScaling logic
  setEstimatedScalingWidths(estimatedWidths);

  const spaceAvailable =
    window.innerWidth -
    chevronWidth -
    reservedSpace -
    // Next style adds 4px of margin on each side of ribbon mode button
    vNextRibbonModeSwitcherLeftRightMargin * 2;

  // If the entire ribbon content fits, don't apply any scaling steps
  if (spaceAvailable > scalingData.width) {
    return scalingState;
  }

  // Determine the scaling step that first allows content to fit in the window
  for (const step in estimatedWidths) {
    if ((estimatedWidths[step] as number) < spaceAvailable) {
      return scaleRibbonDown(
        scalingState,
        parseInt(step, 10) - scalingState.appliedScalingStepCount
      );
    }
  }

  return scaleRibbonDown(
    scalingState,
    scalingState.scalingSteps.length - scalingState.appliedScalingStepCount
  );
}
